import AnalyticFields from '../../enums/AnalyticFields';

const TOP_COUNTRY_LIMIT = 4;
const TOP_SOURCE_LIMIT = 5;
const OTHER_COUNTRY_NAME = 'Others';

const metricFields = [
  AnalyticFields.Request,
  AnalyticFields.Impressions,
  AnalyticFields.Clicks,
  AnalyticFields.Revenue,
];

function roundMetric(value) {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.round(numericValue * 100) / 100;
}

function createEmptyMetrics() {
  return metricFields.reduce((metrics, field) => {
    metrics[field.label] = 0;
    return metrics;
  }, {});
}

function addMetrics(target, row, accountId) {
  metricFields.forEach(field => {
    target[field.label] = roundMetric(
      target[field.label] + getRowValue(row, accountId, field.name)
    );
  });
}

function getRowValue(row, accountId, fieldName) {
  return (
    row?.[`${accountId}.${fieldName}`] ?? row?.[fieldName] ?? row?.[fieldName.toLowerCase()] ?? ''
  );
}

function getId(value, fallback) {
  return String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function getGroupedItem(map, key, name, fallbackKey) {
  if (!map.has(key)) {
    map.set(key, {
      id: getId(key, fallbackKey),
      name,
      metrics: createEmptyMetrics(),
    });
  }

  return map.get(key);
}

function getGroupedCountry(map, countryName) {
  if (!map.has(countryName)) {
    map.set(countryName, createCountry(countryName, map.size));
  }

  return map.get(countryName);
}

function sortByRevenue(items) {
  return [...items].sort(
    (left, right) => Number(right.metrics?.Revenue ?? 0) - Number(left.metrics?.Revenue ?? 0)
  );
}

function sortByMetric(items, metric) {
  return [...items].sort(
    (left, right) => Number(right.metrics?.[metric] ?? 0) - Number(left.metrics?.[metric] ?? 0)
  );
}

function toSortedGeographyList(map) {
  return sortByRevenue(Array.from(map.values()));
}

function createCountry(name, index) {
  return {
    id: getId(name, `country-${index + 1}`),
    name,
    metrics: createEmptyMetrics(),
    regionsMap: new Map(),
    citiesMap: new Map(),
  };
}

function addGeography(country, row, accountId, countryName) {
  const stateName = getRowValue(row, accountId, AnalyticFields.State.name);
  const cityName = getRowValue(row, accountId, AnalyticFields.City.name);

  if (stateName) {
    const state = getGroupedItem(
      country.regionsMap,
      `${countryName}|${stateName}`,
      stateName,
      `region-${country.regionsMap.size + 1}`
    );
    addMetrics(state.metrics, row, accountId);
  }

  if (cityName) {
    const city = getGroupedItem(
      country.citiesMap,
      `${countryName}|${stateName}|${cityName}`,
      cityName,
      `city-${country.citiesMap.size + 1}`
    );
    addMetrics(city.metrics, row, accountId);
  }
}

function finalizeCountry(country) {
  return {
    id: country.id,
    name: country.name,
    metrics: country.metrics,
    regions: toSortedGeographyList(country.regionsMap),
    cities: toSortedGeographyList(country.citiesMap),
  };
}

function addMetricValues(target, source) {
  metricFields.forEach(field => {
    target[field.label] = roundMetric(target[field.label] + Number(source?.[field.label] ?? 0));
  });
}

function mergeGeographyListIntoMap(map, items = [], countryName, fallbackKey) {
  items.forEach(item => {
    const groupedItem = getGroupedItem(
      map,
      `${countryName}|${item.name}`,
      `${item.name} (${countryName})`,
      `${fallbackKey}-${map.size + 1}`
    );

    addMetricValues(groupedItem.metrics, item.metrics);
  });
}

function mergeCountryIntoOther(otherCountry, country) {
  addMetricValues(otherCountry.metrics, country.metrics);

  mergeGeographyListIntoMap(otherCountry.regionsMap, country.regions, country.name, 'other-region');
  mergeGeographyListIntoMap(otherCountry.citiesMap, country.cities, country.name, 'other-city');
}

export function transformCountryGeographyResponse(rows = [], accountId) {
  if (!Array.isArray(rows) || !accountId) {
    return [];
  }

  const countriesMap = rows.reduce((map, row) => {
    const countryName = getRowValue(row, accountId, AnalyticFields.Country.name);
    if (!countryName) {
      return map;
    }

    const country = getGroupedCountry(map, countryName);
    addMetrics(country.metrics, row, accountId);
    addGeography(country, row, accountId, countryName);
    return map;
  }, new Map());

  return sortByRevenue(Array.from(countriesMap.values())).map(finalizeCountry);
}

export function transformSourceMetricsResponse(rows = [], accountId, sourceTransformedData = []) {
  if (!Array.isArray(rows) || !accountId) {
    return [];
  }

  const sourcesMap = rows.reduce((map, row) => {
    const sourceName = getRowValue(row, accountId, AnalyticFields.Source.name);
    if (!sourceName) {
      return map;
    }
    const sourceData = sourceTransformedData.find(source => source?.id == sourceName);

    const source = getGroupedItem(
      map,
      sourceData?.name || sourceName,
      sourceData?.name || sourceName,
      `source-${map.size + 1}`
    );
    addMetrics(source.metrics, row, accountId);
    return map;
  }, new Map());

  return sortByRevenue(Array.from(sourcesMap.values())).slice(0, TOP_SOURCE_LIMIT);
}

export function getTopCountriesWithOtherByMetric(countries = [], metric = 'Revenue') {
  if (!Array.isArray(countries)) {
    return [];
  }

  const sortedCountries = sortByMetric(countries, metric);
  const topCountries = sortedCountries.slice(0, TOP_COUNTRY_LIMIT);
  const remainingCountries = sortedCountries.slice(TOP_COUNTRY_LIMIT);
  const topCountriesWithColors = topCountries.map(country => ({
    ...country,
  }));

  if (!remainingCountries.length) {
    return topCountriesWithColors;
  }

  const otherCountry = remainingCountries.reduce(
    (mergedOther, country) => {
      mergeCountryIntoOther(mergedOther, country);
      return mergedOther;
    },
    createCountry(OTHER_COUNTRY_NAME, TOP_COUNTRY_LIMIT)
  );

  return [...topCountriesWithColors, finalizeCountry(otherCountry, TOP_COUNTRY_LIMIT)];
}
